import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '../lib/api';

/* ============================================================================
   THE BILLING PROFILE

   What turns a screenshot into a RECEIPT: a legal name, a real address, a phone
   number, a signature line. A parent files this. An auditor may read it.

   Deliberately NOT a template builder. Schools are not designers, and a
   drag-and-drop layout editor reliably produces documents that look WORSE than a
   good default. Give them their logo, their details, and a clean layout that
   prints — that is 90% of the value for 10% of the work.
   ========================================================================== */

interface Profile {
  legalName: string | null;
  addressLine1: string | null; addressLine2: string | null;
  city: string | null; state: string | null; pincode: string | null;
  phone: string | null; email: string | null; website: string | null;
  gstin: string | null; pan: string | null;
  bankName: string | null; bankAccountNumber: string | null; bankIfsc: string | null;
  footerNote: string | null; signatoryName: string | null; signatoryTitle: string | null;
}

const EMPTY: Profile = {
  legalName: '', addressLine1: '', addressLine2: '',
  city: '', state: '', pincode: '',
  phone: '', email: '', website: '',
  gstin: '', pan: '',
  bankName: '', bankAccountNumber: '', bankIfsc: '',
  footerNote: '', signatoryName: '', signatoryTitle: '',
};

export function BillingProfilePage() {
  const [p, setP] = useState<Profile | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<Profile>('/api/fees/billing-profile')
      .then((r) => setP({ ...EMPTY, ...r }))
      .catch(() => setP(EMPTY));
  }, []);
  useEffect(load, [load]);

  const set = (k: keyof Profile, v: string) => setP((cur) => (cur ? { ...cur, [k]: v } : cur));

  const save = async () => {
    if (!p) return;
    setBusy(true); setError(null); setSaved(false);
    try {
      await api.put('/api/fees/billing-profile', p);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save.');
    } finally { setBusy(false); }
  };

  if (!p) {
    return <div className="card" style={{ padding: 20 }}><span className="sk" style={{ width: '50%' }} /></div>;
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Fees</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Invoice details</h1>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-accent" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
          </button>
        </div>
      </div>

      <p style={{ color: 'var(--ink-2)', fontSize: 14, marginBottom: 20, maxWidth: '52ch' }}>
        These appear on every invoice and receipt you print. Your logo and colours come from your
        school's branding — this is the rest of what makes it a real document.
      </p>

      {error && (
        <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 16, justifyContent: 'flex-start' }}>
          {error}
        </div>
      )}

      <div className="grid-auto">
        {/* ---- identity ---- */}
        <Section title="The school" hint="As it should appear on a legal document.">
          <Field label="Legal name" hint="Often different from the trading name"
            value={p.legalName} onChange={(v) => set('legalName', v)}
            placeholder="Sri Vidya Educational Trust" />
          <Field label="Address line 1" value={p.addressLine1} onChange={(v) => set('addressLine1', v)} />
          <Field label="Address line 2" value={p.addressLine2} onChange={(v) => set('addressLine2', v)} />
          <div className="form-row-3">
            <Field label="City" value={p.city} onChange={(v) => set('city', v)} />
            <Field label="State" value={p.state} onChange={(v) => set('state', v)} />
            <Field label="Pincode" value={p.pincode} onChange={(v) => set('pincode', v)} />
          </div>
        </Section>

        {/* ---- contact ---- */}
        <Section title="Contact" hint="Printed under the school's name.">
          <Field label="Phone" value={p.phone} onChange={(v) => set('phone', v)} />
          <Field label="Email" value={p.email} onChange={(v) => set('email', v)} />
          <Field label="Website" value={p.website} onChange={(v) => set('website', v)} />
        </Section>

        {/* ---- tax ---- */}
        <Section title="Tax" hint="Leave blank if you don't have one — most schools don't. Blank fields are simply omitted from the receipt.">
          <Field label="GSTIN" value={p.gstin} onChange={(v) => set('gstin', v)} mono />
          <Field label="PAN" value={p.pan} onChange={(v) => set('pan', v)} mono />
        </Section>

        {/* ---- bank ---- */}
        <Section title="Bank transfer" hint="Printed on the invoice, so a parent paying by transfer doesn't have to ring the office.">
          <Field label="Bank name" value={p.bankName} onChange={(v) => set('bankName', v)} />
          <Field label="Account number" value={p.bankAccountNumber} onChange={(v) => set('bankAccountNumber', v)} mono />
          <Field label="IFSC" value={p.bankIfsc} onChange={(v) => set('bankIfsc', v)} mono />
        </Section>

        {/* ---- foot ---- */}
        <Section title="The bottom of the page" hint="Your own words.">
          <div>
            <label className="label">Footer note</label>
            <textarea className="input" rows={3}
              placeholder="Fees once paid are not refundable."
              value={p.footerNote ?? ''} onChange={(e) => set('footerNote', e.target.value)} />
          </div>
          <Field label="Signatory name" value={p.signatoryName} onChange={(v) => set('signatoryName', v)}
            placeholder="Authorised signatory" />
          <Field label="Signatory title" value={p.signatoryTitle} onChange={(v) => set('signatoryTitle', v)}
            placeholder="Principal / Accountant" />
        </Section>
      </div>
    </div>
  );
}

function Section({ title, hint, children }: {
  title: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div className="card" style={{ padding: 18 }}>
      <h3 style={{ fontSize: 15, fontFamily: 'var(--font-display)', marginBottom: 2 }}>{title}</h3>
      {hint && (
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>{hint}</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  );
}

function Field({ label, hint, value, onChange, placeholder, mono }: {
  label: string; hint?: string; value: string | null;
  onChange: (v: string) => void; placeholder?: string; mono?: boolean;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className={`input${mono ? ' mono' : ''}`} value={value ?? ''}
        placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      {hint && <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>{hint}</p>}
    </div>
  );
}
